import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Button, Modal, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { PageLoader } from '../../Components/LoadingSpinner';
import { httpsCallable } from 'firebase/functions';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, functions } from '../../firebase';
import { useUsersData, Userprofile } from '../../Helpers/UsersContext';
import { Plus, ChevronLeft } from 'lucide-react';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { useListDetailScroll } from '../../hooks/useListDetailScroll';
import UserDetailPane from './UserDetailPane';
import { recordActivity } from '../../api/activityApi';
import ExternalUsersTab from './ExternalUsersTab';
import UserGroupsTab from './UserGroupsTab';
import './ManageUsers.css';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useExternalUsers } from '../../hooks/useExternalUsers';
import { useCustomerType } from '../../hooks/useCustomerType';

type ManageUsersTab = 'internal' | 'external' | 'groups' | 'deleted';
const VALID_TABS: ManageUsersTab[] = ['internal', 'external', 'groups', 'deleted'];
const BASE_PATH = '/settings/manage-users';


// No password: the function sets a random one and emails the user a link to choose their own
interface CreateUserRequest {
    name: string;
    email: string;
    admin?: boolean;
    superadmin?: boolean;
    accessToWeb?: boolean;
    groups?: string[];
}

interface UpdateUserRequest {
    uid: string;
    name?: string;
    admin?: boolean;
    superadmin?: boolean;
    accessToWeb?: boolean;
}

const ManageUsers: React.FC = () => {
    const [user] = useAuthState(auth);
    const { users, loadingUsers, usersError, refreshUsers } = useUsersData();
    const { users: externalUsers } = useExternalUsers();
    // Användargrupper only narrow the iOS views on stiftelse instances (null = unset, treated as stiftelse)
    const customerType = useCustomerType();
    const showGroupsTab = customerType === null || customerType === 'stiftelse';
    const location = useLocation();
    const navigate = useNavigate();

    // Tab and selected user are driven by the URL: /settings/manage-users/<tab>/<uid>
    const pathParts = location.pathname.split('/').filter(Boolean);
    const manageIndex = pathParts.indexOf('manage-users');
    const tabFromUrl = manageIndex >= 0 ? pathParts[manageIndex + 1] : undefined;
    const uidFromUrl = manageIndex >= 0 ? pathParts[manageIndex + 2] : undefined;
    const activeTab: ManageUsersTab = VALID_TABS.includes(tabFromUrl as ManageUsersTab)
        ? (tabFromUrl as ManageUsersTab)
        : 'internal';

    // The tab path, or one user/group in it (null = the list)
    const tabPath = (tab: ManageUsersTab, key?: string | null) => (key ? `${BASE_PATH}/${tab}/${key}` : `${BASE_PATH}/${tab}`);
    const clearSelection = () => navigate(`${BASE_PATH}/${activeTab}`);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingUser, setEditingUser] = useState<Userprofile | null>(null);
    const [deletingUser, setDeletingUser] = useState<Userprofile | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isSuperadmin, setIsSuperadmin] = useState(false);

    // Modal-specific error states
    const [createModalError, setCreateModalError] = useState<string | null>(null);
    const [editModalError, setEditModalError] = useState<string | null>(null);
    const [deleteModalError, setDeleteModalError] = useState<string | null>(null);

    // Form states
    const [newUser, setNewUser] = useState<CreateUserRequest>({
        name: '',
        email: '',
        admin: false,
        superadmin: false,
        accessToWeb: false
    });

    const [editUser, setEditUser] = useState<UpdateUserRequest>({
        uid: '',
        name: '',
        admin: false,
        superadmin: false,
        accessToWeb: false
    });

    const [replacementName, setReplacementName] = useState<string>('Borttagen användare');
    const [editDeletedName, setEditDeletedName] = useState<string>('');

    // Firebase Functions
    const createUserFn = httpsCallable(functions, 'createUser');
    const updateAdminStatusFn = httpsCallable(functions, 'updateAdminStatus');
    const updateSuperadminStatusFn = httpsCallable(functions, 'updateSuperadminStatus');
    const updateWebAccessFn = httpsCallable(functions, 'updateWebAccess');
    const updateUserNameFn = httpsCallable(functions, 'updateUserName');
    const deleteUserFn = httpsCallable(functions, 'deleteUser');
    const updateDeletedUserNameFn = httpsCallable(functions, 'updateDeletedUserName');

    // Check if current user is admin/superadmin
    useEffect(() => {
        if (user && users) {
            const currentUser = users.find(u => u.key === user.uid);
            // Superadmins have all admin privileges
            setIsAdmin(currentUser?.admin || currentUser?.superadmin || false);
            setIsSuperadmin(currentUser?.superadmin || false);
        }
    }, [user, users]);

    // Set document title
    useDocumentTitle('Hantera användare');

    // Filter and sort users alphabetically by name
    const sortByName = (a: Userprofile, b: Userprofile) => a.name.localeCompare(b.name, 'sv');
    const activeUsers = (users?.filter(u => !u.deleted && !u.isExternal) || []).sort(sortByName);
    const deletedUsers = (users?.filter(u => u.deleted) || []).sort(sortByName);

    // Get displayed users based on active tab
    const displayedUsers = activeTab === 'deleted' ? deletedUsers : activeUsers;

    // Selected user is derived from the URL (internal/deleted tabs only)
    const selectedUser = activeTab === 'external' || activeTab === 'groups'
        ? null
        : displayedUsers.find(u => u.key === uidFromUrl) || null;

    // Phones show either the user list or the open user, never both
    const isSmallScreen = useIsSmallScreen();
    const mobileView = isSmallScreen && selectedUser ? 'detail' : 'list';
    const { containerRef, rememberListScroll } = useListDetailScroll<HTMLDivElement>(mobileView === 'detail');

    // On phones the tab bar scrolls sideways; bring the active tab into view.
    // Runs after every render until it has scrolled for the current tab, since
    // the bar only mounts once loading is done.
    const tabBarRef = useRef<HTMLDivElement>(null);
    const scrolledForTabRef = useRef<string | null>(null);
    useLayoutEffect(() => {
        const bar = tabBarRef.current;
        const active = bar?.querySelector<HTMLElement>('.manage-users-tab.active');
        if (!bar || !active || scrolledForTabRef.current === activeTab) return;
        scrolledForTabRef.current = activeTab;
        if (bar.scrollWidth > bar.clientWidth) {
            bar.scrollLeft = active.offsetLeft - (bar.clientWidth - active.offsetWidth) / 2;
        }
    });

    // Helper to get user name by UID
    const getUserName = (uid?: string) => {
        if (!uid) return 'Okänd';
        const foundUser = users?.find(u => u.key === uid);
        return foundUser?.name || uid;
    };

    // Get initials for avatar
    const getInitials = (name: string) => {
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setCreateModalError(null);

        try {
            const result = await createUserFn(newUser);
            const { message, emailSent, user: createdUser } = result.data as { message?: string; emailSent?: boolean; user?: { key?: string } };
            recordActivity('user.created', { targetKey: createdUser?.key });
            setNewUser({ name: '', email: '', admin: false, superadmin: false, accessToWeb: false });
            await refreshUsers();
            setShowCreateModal(false);
            // The account exists even when the welcome email failed; the message says what to do
            if (emailSent === false) {
                setError(message || 'Användaren har skapats men välkomstmailet kunde inte skickas.');
            } else {
                setSuccess(message || 'Användaren har skapats och ett välkomstmail har skickats.');
            }
        } catch (err: any) {
            setCreateModalError(err.message || 'Ett fel uppstod vid skapande av användare');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setEditModalError(null);

        try {
            const isDeletedUser = editingUser?.deleted;
            let changed = false;

            if (isDeletedUser) {
                // For deleted users, only update name
                if (editDeletedName !== editingUser?.name) {
                    await updateDeletedUserNameFn({ uid: editUser.uid, replacementName: editDeletedName });
                    changed = true;
                }
            } else {
                // For active users
                if (editUser.name && editUser.name !== editingUser?.name) {
                    await updateUserNameFn({ uid: editUser.uid, name: editUser.name });
                    changed = true;
                }
                if (editUser.admin !== editingUser?.admin) {
                    await updateAdminStatusFn({ uid: editUser.uid, admin: editUser.admin });
                    changed = true;
                }
                if (editUser.superadmin !== editingUser?.superadmin) {
                    await updateSuperadminStatusFn({ uid: editUser.uid, superadmin: editUser.superadmin });
                    changed = true;
                }
                if (editUser.accessToWeb !== editingUser?.accessToWeb) {
                    await updateWebAccessFn({ uid: editUser.uid, accessToWeb: editUser.accessToWeb });
                    changed = true;
                }
            }
            // One entry per save, however many callables it took
            if (changed) recordActivity('user.updated', { targetKey: editUser.uid });

            await refreshUsers();
            setShowEditModal(false);
            setEditingUser(null);
            setSuccess('Användare uppdaterad framgångsrikt!');
        } catch (err: any) {
            console.error('Error updating user:', err);
            setEditModalError(err.message || 'Ett fel uppstod vid uppdatering av användare');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!deletingUser) return;

        setLoading(true);
        setDeleteModalError(null);

        try {
            await deleteUserFn({
                uid: deletingUser.key,
                replacementName: replacementName
            });
            recordActivity('user.deleted', { targetKey: deletingUser.key });
            await refreshUsers();
            setShowDeleteModal(false);
            setDeletingUser(null);
            setReplacementName('Borttagen användare');
            clearSelection();
            setSuccess('Användare raderad framgångsrikt!');
        } catch (err: any) {
            setDeleteModalError(err.message || 'Ett fel uppstod vid radering av användare');
        } finally {
            setLoading(false);
        }
    };

    const openCreateModal = () => {
        setNewUser({ name: '', email: '', admin: false, superadmin: false, accessToWeb: false });
        setCreateModalError(null);
        setShowCreateModal(true);
    };

    const openEditModal = (userData: Userprofile) => {
        setEditingUser(userData);
        if (userData.deleted) {
            setEditDeletedName(userData.name);
        } else {
            setEditUser({
                uid: userData.key,
                name: userData.name,
                // Superadmin implies admin; saving repairs a stored superadmin without the admin flag
                admin: userData.admin || userData.superadmin || false,
                superadmin: userData.superadmin || false,
                accessToWeb: userData.accessToWeb || false
            });
        }
        setEditModalError(null);
        setShowEditModal(true);
    };

    const openDeleteModal = (userData: Userprofile) => {
        setDeletingUser(userData);
        setReplacementName('Borttagen användare');
        setDeleteModalError(null);
        setShowDeleteModal(true);
    };

    // Canonicalize the URL so a tab is always present
    if (manageIndex >= 0 && !VALID_TABS.includes(tabFromUrl as ManageUsersTab)) {
        return <Navigate to={`${BASE_PATH}/internal`} replace />;
    }
    if (activeTab === 'groups' && !showGroupsTab) {
        return <Navigate to={`${BASE_PATH}/internal`} replace />;
    }

    if (!isAdmin) {
        return (
            <div className="manage-users-page-wrapper">
                <Alert variant="warning">
                    <Alert.Heading>Åtkomst nekad</Alert.Heading>
                    <p>Du har inte behörighet att hantera användare. Endast administratörer kan komma åt denna sida.</p>
                </Alert>
            </div>
        );
    }

    if (loadingUsers) {
        return (
            <div className="manage-users-page-wrapper">
                <PageLoader text="Laddar användare..." />
            </div>
        );
    }

    if (usersError) {
        return (
            <div className="manage-users-page-wrapper">
                <Alert variant="danger">
                    <Alert.Heading>Fel vid laddning av användare</Alert.Heading>
                    <p>{usersError.message}</p>
                    <Button variant="outline-danger" onClick={refreshUsers}>
                        Försök igen
                    </Button>
                </Alert>
            </div>
        );
    }

    return (
        <div className="manage-users-page-wrapper">
            {/* Tab bar */}
            <div className="manage-users-tab-bar" ref={tabBarRef}>
                <Link
                    to={tabPath('internal')}
                    className={`manage-users-tab ${activeTab === 'internal' ? 'active' : ''}`}
                    aria-current={activeTab === 'internal' ? 'page' : undefined}
                >
                    Interna användare ({activeUsers.length})
                </Link>
                <Link
                    to={tabPath('external')}
                    className={`manage-users-tab ${activeTab === 'external' ? 'active' : ''}`}
                    aria-current={activeTab === 'external' ? 'page' : undefined}
                >
                    Externa användare ({externalUsers.length})
                </Link>
                {showGroupsTab && (
                    <Link
                        to={tabPath('groups')}
                        className={`manage-users-tab ${activeTab === 'groups' ? 'active' : ''}`}
                        aria-current={activeTab === 'groups' ? 'page' : undefined}
                    >
                        Användargrupper
                    </Link>
                )}
                <Link
                    to={tabPath('deleted')}
                    className={`manage-users-tab ${activeTab === 'deleted' ? 'active' : ''}`}
                    aria-current={activeTab === 'deleted' ? 'page' : undefined}
                >
                    Borttagna användare ({deletedUsers.length})
                </Link>
            </div>

            {/* Global alerts */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
                    {error}
                </Alert>
            )}
            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-3">
                    {success}
                </Alert>
            )}

            {/* External users tab */}
            {activeTab === 'external' && (
                <ExternalUsersTab
                    getUserName={getUserName}
                    selectedUid={uidFromUrl}
                    userPath={(uid) => tabPath('external', uid)}
                    onSelectUser={(uid) => navigate(tabPath('external', uid))}
                />
            )}

            {/* User groups tab (stiftelse only) */}
            {activeTab === 'groups' && (
                <UserGroupsTab
                    users={activeUsers}
                    selectedGroupKey={uidFromUrl}
                    groupPath={(key) => tabPath('groups', key)}
                    onMembershipChanged={refreshUsers}
                />
            )}

            {/* Split-pane content for internal users */}
            {(activeTab === 'internal' || activeTab === 'deleted') && (
            <div className="manage-users-page" ref={containerRef} data-mobile-view={mobileView}>
                {/* Left Pane - User List */}
                <div className="manage-users-left-pane" onClickCapture={rememberListScroll}>
                    <div className="left-pane-header">
                        {activeTab === 'internal' && (
                            <Button variant="primary" size="sm" onClick={openCreateModal}>
                                <Plus size={16} className="me-1" />
                                Skapa ny
                            </Button>
                        )}
                    </div>
                    <div className="user-list-container">
                        {activeTab === 'deleted' && deletedUsers.length > 0 && (
                            <div className="deleted-users-info">
                                Dessa användare har raderats och kan inte logga in.
                            </div>
                        )}

                        {displayedUsers.length === 0 ? (
                            <div className="empty-user-list">
                                {activeTab === 'internal'
                                    ? 'Inga interna användare'
                                    : 'Inga borttagna användare'}
                            </div>
                        ) : (
                            displayedUsers.map(userData => (
                                <Link
                                    key={userData.key}
                                    to={tabPath(activeTab, userData.key)}
                                    className={`user-item ${selectedUser?.key === userData.key ? 'selected' : ''}`}
                                    aria-current={selectedUser?.key === userData.key ? 'page' : undefined}
                                >
                                    <div className={`user-item-avatar ${userData.admin ? 'admin' : ''} ${userData.deleted ? 'deleted' : ''}`}>
                                        {getInitials(userData.name)}
                                    </div>
                                    <div className="user-item-info">
                                        <div className="user-item-name">{userData.name}</div>
                                        {!userData.deleted && (
                                            <div className="user-item-meta">
                                                {(() => {
                                                    const lastActive = userData.lastActive || userData.lastAppStart;
                                                    return lastActive
                                                        ? `Aktiv ${new Date(lastActive).toLocaleDateString('sv-SE')}`
                                                        : 'Aldrig aktiv';
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                    {!userData.deleted && (
                                        <div className="user-item-badges">
                                            {userData.admin && (
                                                <Badge bg="" style={{ backgroundColor: '#001D60' }} title="Administratör">A</Badge>
                                            )}
                                            {userData.accessToWeb && (
                                                <Badge bg="" style={{ backgroundColor: '#28aede' }} title="Webbåtkomst">W</Badge>
                                            )}
                                        </div>
                                    )}
                                </Link>
                            ))
                        )}
                    </div>
                </div>

                {/* Right Pane - Detail */}
                <div className="manage-users-right-pane">
                    {mobileView === 'detail' && (
                        <Link to={tabPath(activeTab)} className="manage-users-back-button">
                            <ChevronLeft size={20} aria-hidden="true" />
                            Alla användare
                        </Link>
                    )}
                    {selectedUser ? (
                        <UserDetailPane
                            user={selectedUser}
                            onEdit={openEditModal}
                            onDelete={openDeleteModal}
                            currentUserId={user?.uid}
                            isDeletedUser={activeTab === 'deleted'}
                            getUserName={getUserName}
                            currentUserIsSuperadmin={isSuperadmin}
                        />
                    ) : (
                        <div className="empty-state">
                            <div className="text-muted">
                                Välj en användare från listan för att visa detaljer
                            </div>
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* Create User Modal */}
            <Modal show={showCreateModal} onHide={() => {
                setShowCreateModal(false);
                setCreateModalError(null);
            }} size="lg" dialogClassName="manage-users-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Skapa ny användare</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleCreateUser}>
                    <Modal.Body>
                        {createModalError && (
                            <Alert variant="danger" dismissible onClose={() => setCreateModalError(null)}>
                                {createModalError}
                            </Alert>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>E-postadress</Form.Label>
                            <Form.Control
                                type="email"
                                value={newUser.email}
                                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                placeholder="Ange användarens e-postadress"
                                required
                            />
                            <Form.Text className="text-muted">
                                E-postadress som används för inloggning. Välkomstmailet skickas hit.
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Namn</Form.Label>
                            <Form.Control
                                type="text"
                                value={newUser.name}
                                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                                placeholder="Ange användarens namn"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="create-user-admin">
                            <Form.Check
                                type="checkbox"
                                label="Administratör"
                                checked={newUser.admin}
                                disabled={newUser.superadmin}
                                onChange={(e) => setNewUser({ ...newUser, admin: e.target.checked })}
                            />
                            {newUser.superadmin && (
                                <Form.Text className="text-muted">
                                    En superadmin är alltid administratör.
                                </Form.Text>
                            )}
                        </Form.Group>
                        {isSuperadmin && (
                            <Form.Group className="mb-3" controlId="create-user-superadmin">
                                <Form.Check
                                    type="checkbox"
                                    label="Superadmin"
                                    checked={newUser.superadmin}
                                    // Superadmin implies admin: checking it also grants admin
                                    onChange={(e) => setNewUser({
                                        ...newUser,
                                        superadmin: e.target.checked,
                                        admin: e.target.checked || newUser.admin,
                                    })}
                                />
                            </Form.Group>
                        )}
                        <Form.Group className="mb-3" controlId="create-user-web">
                            <Form.Check
                                type="checkbox"
                                label="Webbåtkomst"
                                checked={newUser.accessToWeb}
                                onChange={(e) => setNewUser({ ...newUser, accessToWeb: e.target.checked })}
                            />
                        </Form.Group>
                        <Alert variant="info" className="mt-3">
                            <small>
                                <strong>Obs:</strong> Användaren får ett välkomstmail med en länk för att välja sitt
                                eget lösenord. Du behöver inte ange eller skicka något lösenord.
                                Länken gäller en kort tid; har den slutat gälla väljer användaren "Glömt lösenord?" vid inloggningen.
                            </small>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => {
                            setShowCreateModal(false);
                            setCreateModalError(null);
                        }}>
                            Avbryt
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Skapar...' : 'Skapa användare'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Edit User Modal */}
            <Modal show={showEditModal} onHide={() => {
                setShowEditModal(false);
                setEditingUser(null);
                setEditModalError(null);
            }} dialogClassName="manage-users-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingUser?.deleted ? 'Redigera namn' : 'Redigera användare'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateUser}>
                    <Modal.Body>
                        {editModalError && (
                            <Alert variant="danger" dismissible onClose={() => setEditModalError(null)}>
                                {editModalError}
                            </Alert>
                        )}
                        {editingUser?.deleted ? (
                            <>
                                <Alert variant="info">
                                    Detta namn visas i applikationen där användaren tidigare refererades.
                                </Alert>
                                <Form.Group className="mb-3">
                                    <Form.Label>Namn</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editDeletedName}
                                        onChange={(e) => setEditDeletedName(e.target.value)}
                                        placeholder="Ange namn"
                                        required
                                    />
                                    <Form.Text className="text-muted">
                                        UID: {editingUser?.key}
                                    </Form.Text>
                                </Form.Group>
                            </>
                        ) : (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Namn</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editUser.name}
                                        onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                                        placeholder="Ange användarens namn"
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="edit-user-admin">
                                    <Form.Check
                                        type="checkbox"
                                        label="Administratör"
                                        checked={editUser.admin}
                                        disabled={editUser.superadmin}
                                        onChange={(e) => setEditUser({ ...editUser, admin: e.target.checked })}
                                    />
                                    {editUser.superadmin && (
                                        <Form.Text className="text-muted">
                                            En superadmin är alltid administratör.
                                        </Form.Text>
                                    )}
                                </Form.Group>
                                {isSuperadmin && (
                                    <Form.Group className="mb-3" controlId="edit-user-superadmin">
                                        <Form.Check
                                            type="checkbox"
                                            label="Superadmin"
                                            checked={editUser.superadmin}
                                            // Superadmin implies admin: checking it also grants admin
                                            onChange={(e) => setEditUser({
                                                ...editUser,
                                                superadmin: e.target.checked,
                                                admin: e.target.checked || editUser.admin,
                                            })}
                                        />
                                    </Form.Group>
                                )}
                                <Form.Group className="mb-3" controlId="edit-user-web">
                                    <Form.Check
                                        type="checkbox"
                                        label="Webbåtkomst"
                                        checked={editUser.accessToWeb}
                                        onChange={(e) => setEditUser({ ...editUser, accessToWeb: e.target.checked })}
                                    />
                                </Form.Group>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => {
                            setShowEditModal(false);
                            setEditingUser(null);
                            setEditModalError(null);
                        }}>
                            Avbryt
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Uppdaterar...' : 'Spara'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete User Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => {
                setShowDeleteModal(false);
                setDeletingUser(null);
                setDeleteModalError(null);
                setReplacementName('Borttagen användare');
            }} size="lg" dialogClassName="manage-users-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Radera användare</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleDeleteUser}>
                    <Modal.Body>
                        {deleteModalError && (
                            <Alert variant="danger" dismissible onClose={() => setDeleteModalError(null)}>
                                {deleteModalError}
                            </Alert>
                        )}
                        <Alert variant="warning">
                            <Alert.Heading>Är du säker på att du vill radera denna användare?</Alert.Heading>
                            <p className="mb-2"><strong>{deletingUser?.name}</strong> ({deletingUser?.key})</p>
                            <hr />
                            <p className="mb-1">Följande kommer att hända:</p>
                            <ul className="mb-0">
                                <li>Användaren kan inte logga in längre</li>
                                <li>All personlig data, grupper och behörigheter kommer att tas bort</li>
                                <li>Ersättningsnamnet kommer att visas där användarens namn tidigare visades</li>
                                <li>Historiska referenser förblir intakta</li>
                            </ul>
                        </Alert>
                        <Form.Group className="mb-3">
                            <Form.Label>Ersättningsnamn</Form.Label>
                            <Form.Control
                                type="text"
                                value={replacementName}
                                onChange={(e) => setReplacementName(e.target.value)}
                                placeholder="Ange ersättningsnamn"
                                required
                            />
                            <Form.Text className="text-muted">
                                Detta namn kommer att visas i applikationen istället för användarens riktiga namn.
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => {
                            setShowDeleteModal(false);
                            setDeletingUser(null);
                            setDeleteModalError(null);
                            setReplacementName('Borttagen användare');
                        }}>
                            Avbryt
                        </Button>
                        <Button variant="danger" type="submit" disabled={loading}>
                            {loading ? 'Raderar...' : 'Radera användare'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default ManageUsers;
